import { Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs';
import { AuthSession } from '../../auth/auth-session';
import { beginSignIn, ensureAccessToken, getAuthRedirectError } from '../../auth/ensure-signed-in';
import { moduleLinkFor } from '../../constants/screen-registry';
import { ShellNavService } from '../../services/shell-nav.service';
import { Sidebar } from '../sidebar/sidebar';

@Component({
  selector: 'app-shell',
  imports: [MatButtonModule, MatProgressSpinnerModule, RouterOutlet, Sidebar],
  templateUrl: './shell.html',
  styleUrl: './shell.scss'
})
export class Shell implements OnInit {
  private readonly authSession = inject(AuthSession);
  private readonly destroyRef = inject(DestroyRef);
  private readonly msal = inject(MsalService, { optional: true });
  private readonly router = inject(Router);
  private readonly shellNav = inject(ShellNavService);

  readonly authError = this.authSession.error;
  readonly authenticated = this.authSession.authenticated;
  readonly collapsed = signal(false);
  readonly navError = this.shellNav.error;
  readonly navigationReady = this.shellNav.loaded;

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.navigateToFirstTabIfNeeded());

    effect(() => {
      if (this.authenticated() && this.shellNav.loaded()) {
        this.navigateToFirstTabIfNeeded();
      }
    });

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void this.confirmSession();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisible));
  }

  ngOnInit(): void {
    void this.confirmSession();
  }

  signInAgain(): void {
    if (!this.msal) {
      return;
    }

    this.authSession.markSignedOut(null);
    void beginSignIn(this.msal.instance);
  }

  toggleCollapsed(): void {
    this.collapsed.update((value) => !value);
  }

  private async confirmSession(): Promise<void> {
    if (!this.msal) {
      this.authSession.markSignedIn();
      this.loadNavigation();
      return;
    }

    try {
      const signedIn = await ensureAccessToken(this.msal.instance);
      if (!signedIn) {
        this.authSession.markSignedOut(getAuthRedirectError());
        return;
      }

      this.authSession.markSignedIn();
      this.loadNavigation();
    } catch (error) {
      if (this.authSession.authenticated()) {
        return;
      }

      const message = error instanceof Error ? error.message : 'Sign-in failed.';
      this.authSession.markSignedOut(message);
    }
  }

  private isPostAuthRoute(): boolean {
    const url = this.router.url.split('#')[0].split('?')[0];
    return url === '' || url === '/' || url === '/auth/callback';
  }

  private loadNavigation(): void {
    if (this.shellNav.loaded()) {
      return;
    }

    this.shellNav.load();
  }

  private navigateToFirstTabIfNeeded(): void {
    if (!this.shellNav.loaded() || !this.isPostAuthRoute()) {
      return;
    }

    const first = this.shellNav.firstRoutableItem();
    if (first) {
      void this.router.navigate(moduleLinkFor(first), { replaceUrl: true });
      return;
    }

    void this.router.navigateByUrl('/monitored-resources', { replaceUrl: true });
  }
}
